import { connect } from 'react-redux'
import { getModalData, clearModal } from '@hixme/modal'
import { getPersonSelector } from 'store/modules/persons/selectors'
import {
  getDocumentPresignedUploadUrl,
  replaceDocumentUpload,
  markDocumentUploadComplete,
  uploadFileToPresignedUrl,
  getDocumentUploadDownloadUrl,
  updateDocumentRequestStatus,
} from 'modules/api/enrollment'
import {
  normalizeDocumentRequestStatus,
  isDocumentRequestCompleted,
  getDocumentRequestCategoryKey,
  computeFileSha256,
} from '../utils'
import ProvideDocumentModal from './ProvideDocumentModal'

const mapStateToProps = (state) => {
  const data = getModalData(state) || {}
  const action = data.action
  const category = action ? getDocumentRequestCategoryKey(action) : null
  const planNameSnapshot = action?.planNameSnapshot ?? ''
  const carrierNameSnapshot = action?.carrierNameSnapshot ?? ''
  const requestForPeopleNames = Array.isArray(action?.requestedPersonNames)
    ? action.requestedPersonNames
    : Array.isArray(action?.requestForPeopleNames)
    ? action.requestForPeopleNames
    : []
  const uploadedDocuments = Array.isArray(data.uploadedDocuments) ? data.uploadedDocuments : []
  const isReplace = Boolean(data.isReplace)
  const replaceDocumentUploadId = data.replaceDocumentUploadId || null
  const needsMoreDocumentsNotice = Boolean(data.needsMoreDocumentsNotice)
  const onDocumentRequestStatusUpdateFailed =
    typeof data.onDocumentRequestStatusUpdateFailed === 'function'
      ? data.onDocumentRequestStatusUpdateFailed
      : null
  const uploadsNotAllowedReason =
    action && isDocumentRequestCompleted(action)
      ? 'This document request is complete. You cannot add, replace, or delete files from this screen.'
      : null
  return {
    title: data.title || 'Provide document',
    action,
    actionIndex: data.actionIndex,
    employeePublicKey: data.employeePublicKey,
    person: getPersonSelector(state),
    onUploadSuccess: data.onUploadSuccess,
    category: category || null,
    planNameSnapshot: planNameSnapshot || null,
    carrierNameSnapshot: carrierNameSnapshot || null,
    requestForPeopleNames,
    uploadedDocuments,
    isReplace,
    replaceDocumentUploadId,
    needsMoreDocumentsNotice,
    onDocumentRequestStatusUpdateFailed,
    uploadsNotAllowedReason,
  }
}

const mapDispatchToProps = (dispatch) => ({
  onClose: () => dispatch(clearModal()),
  onSubmit: async (
    files,
    action,
    onUploadSuccess,
    actionIndex,
    isReplace,
    replaceDocumentUploadId,
    onDocumentRequestStatusUpdateFailed
  ) => {
    if (!files || !Array.isArray(files) || files.length === 0 || !action) return
    const documentRequestId = action.id || action.documentRequestId
    if (!documentRequestId) {
      throw new Error('Upload for this category is not available yet.')
    }
    // Replace mode: only one upload can be replaced at a time; use first file only to avoid API errors
    const filesToProcess = isReplace && replaceDocumentUploadId ? files.slice(0, 1) : files
    for (const file of filesToProcess) {
      const contentType = file.type || 'application/octet-stream'
      const presignedResponse =
        isReplace && replaceDocumentUploadId
          ? await replaceDocumentUpload({
              documentUploadId: replaceDocumentUploadId,
              fileName: file.name,
              contentType,
            })
          : await getDocumentPresignedUploadUrl({
              documentRequestId,
              fileName: file.name,
              contentType,
            })
      const url =
        presignedResponse.presignedUploadUrl ||
        presignedResponse.uploadUrl ||
        presignedResponse.preSignedUrl
      const documentUploadId = presignedResponse.documentUploadId
      if (!url || !documentUploadId) {
        throw new Error('Invalid presigned URL response')
      }
      await uploadFileToPresignedUrl(url, file)
      const sha256 = await computeFileSha256(file)
      await markDocumentUploadComplete(documentUploadId, {
        sizeBytes: file.size,
        sha256,
      })
    }
    let statusUpdateError = null
    if (normalizeDocumentRequestStatus(action.status) === 'NEED_MORE_INFO') {
      try {
        await updateDocumentRequestStatus(String(documentRequestId), {
          status: 'RESUBMITTED',
        })
      } catch (err) {
        statusUpdateError = err
      }
    }
    dispatch(clearModal())
    if (typeof onUploadSuccess === 'function') onUploadSuccess(action, actionIndex)
    if (statusUpdateError && typeof onDocumentRequestStatusUpdateFailed === 'function') {
      onDocumentRequestStatusUpdateFailed(statusUpdateError)
    }
  },
})

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  onClose: dispatchProps.onClose,
  onSubmit: (files) =>
    dispatchProps.onSubmit(
      Array.isArray(files) ? files : [files],
      stateProps.action,
      stateProps.onUploadSuccess,
      stateProps.actionIndex,
      stateProps.isReplace,
      stateProps.replaceDocumentUploadId,
      stateProps.onDocumentRequestStatusUpdateFailed
    ),
  onDownloadDocument: (documentUploadId) =>
    getDocumentUploadDownloadUrl(documentUploadId).then((url) => {
      window.location.assign(url)
    }),
})

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ProvideDocumentModal)
